import React, { useState, useEffect } from "react";
import PropTypes from "prop-types";
import { A11y } from "@churchofjesuschrist/eden-buttons";
import { grey3, grey20, grey30, grey40, grey60, text120, blue25, blue30, blue40, white, spacing24, spacing32 } from "@churchofjesuschrist/eden-style-constants";
import { mediaBreak600w } from "@churchofjesuschrist/eden-styled-helpers";
import styled from "styled-components";
import { Select as EdenSelect } from "@churchofjesuschrist/eden-form-parts";
import { renderToStaticMarkup } from "react-dom/server";
import { ArrowDropDown } from "@churchofjesuschrist/eden-icons";
const iconToString = encodeURIComponent(renderToStaticMarkup( /*#__PURE__*/React.createElement(ArrowDropDown, {
  color: white,
  size: "1.5rem"
})));

const getLabel = function () {
  let {
    props: {
      value,
      label = value
    }
  } = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : {
    props: {}
  };
  return label;
};

const Select = styled(EdenSelect).withConfig({
  componentId: "sc-o7485e-0"
})(["background-color:", ";border-radius:2px;border-color:", ";color:", ";background-image:url(\"data:image/svg+xml; utf8, ", "\");&:hover{background-color:", ";}&:active{background-color:", ";}"], grey40, grey40, white, iconToString, grey60, text120);
const DesktopLayout = styled.div.withConfig({
  componentId: "sc-o7485e-1"
})(["display:none;", ";"], mediaBreak600w`
    display: block;
  `);
const MobileLayout = styled.div.withConfig({
  componentId: "sc-o7485e-2"
})(["display:block;", ";"], mediaBreak600w`
    display: none;
  `);
const Button = styled(_ref => {
  let { ...props
  } = _ref;
  delete props.selected;
  return /*#__PURE__*/React.createElement(A11y, props);
}).withConfig({
  componentId: "sc-o7485e-3"
})(["position:relative;height:", ";padding:0 ", ";background:", ";border:1px solid ", ";& + &{border-inline-start:none;}&:first-of-type{border-start-start-radius:2px;border-end-start-radius:2px;}&:last-of-type{border-start-end-radius:2px;border-end-end-radius:2px;}&:hover{background:", ";}&:focus-visible{outline:2px solid #2360c5;z-index:2;}", ";&:active{background:", ";color:", ";}&:disabled{background:", ";color:", ";}"], spacing32, spacing24, white, grey30, grey3, _ref2 => {
  let {
    selected
  } = _ref2;
  return selected ? `
  background: ${blue30};
  color: ${white};

  &:hover {
    background: ${blue25};
    color: ${white};
  }
` : "";
}, blue40, white, white, grey20);
const Buttons = styled.div.withConfig({
  componentId: "sc-o7485e-4"
})(["display:inline-flex;max-width:100%;"]);

const ButtonBar = _ref3 => {
  var _items$find, _items$find$props;

  let {
    "aria-label": ariaLabel = "Options",
    defaultValue,
    onChange = x => x,
    children,
    ...props
  } = _ref3;
  const items = React.Children.toArray(children);
  const [value, setValue] = useState(defaultValue || ((_items$find = items.find(item => !item.props.disabled)) === null || _items$find === void 0 ? void 0 : (_items$find$props = _items$find.props) === null || _items$find$props === void 0 ? void 0 : _items$find$props.value));
  useEffect(() => {
    onChange(value);
  }, [value, onChange]);
  const buttons = items.map(item => /*#__PURE__*/React.createElement(Button, {
    key: item.props.value,
    onClick: () => setValue(item.props.value),
    selected: item.props.value === value,
    disabled: item.props.disabled
  }, getLabel(item)));
  return /*#__PURE__*/React.createElement("div", props, /*#__PURE__*/React.createElement(MobileLayout, null, /*#__PURE__*/React.createElement(Select, {
    small: true,
    value: value,
    onChange: event => setValue(event.target.value),
    "aria-label": ariaLabel
  }, items.map(item => /*#__PURE__*/React.createElement("option", {
    key: item.props.value,
    value: item.props.value,
    disabled: item.props.disabled
  }, getLabel(item))))), /*#__PURE__*/React.createElement(DesktopLayout, null, /*#__PURE__*/React.createElement(Buttons, null, buttons)), items.filter(item => value === item.props.value));
};

ButtonBar.propTypes = {
  /** Children should be `<Option>` elements.  */
  children: PropTypes.node.isRequired,

  /** Callback to execute when value changes. Callback function will receive new value. */
  onChange: PropTypes.func,

  /** The default selected value. If not set, it will be the `value` prop of the first `<Option>` child. */
  defaultValue: PropTypes.string,

  /** The label for the mobile select view */
  "aria-label": PropTypes.string
};
export default ButtonBar;